import React from 'react';

/**
 * Flatten nested extraction JSON to [{ key, value }] rows using dot/bracket
 * paths, mirroring the backend's flatten rules (null values skipped).
 * e.g. { accounts: [{ balance: 5 }] } -> [{ key: 'accounts[0].balance', value: 5 }]
 */
export const flattenDataPoints = (data) => {
  const rows = [];
  const walk = (node, path) => {
    if (node === null || node === undefined) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((value, idx) => walk(value, `${path}[${idx}]`));
      return;
    }
    if (typeof node === 'object') {
      Object.entries(node).forEach(([key, value]) => {
        walk(value, path ? `${path}.${key}` : key);
      });
      return;
    }
    rows.push({ key: path, value: node });
  };
  walk(data || {}, '');
  return rows;
};

/**
 * 'gross_income' -> 'Gross Income'; 'accounts[0].balance' -> 'Accounts [0] › Balance'
 */
export const humanizeKey = (key) => {
  return String(key || '')
    .split('.')
    .map((segment) =>
      segment
        .replace(/\[(\d+)\]/g, ' [$1]')
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
    )
    .join(' › ');
};

/**
 * What happened when the extraction prompt ran on a document:
 *   extracted       - data points exist
 *   not_applicable  - the prompt said the document isn't its type (reason given)
 *   failed          - the response wasn't parseable, or the step errored
 *   none            - no extraction prompt ran
 * Every Extract_* prompt answers {"error": "not_applicable", "reason": ...}
 * when the document doesn't match; rows saved before the backend handled
 * that still carry it as data points, so both shapes are recognized.
 */
export const getExtractionStatus = (dataPoints, details) => {
  const isSentinel = dataPoints && typeof dataPoints === 'object' && !Array.isArray(dataPoints)
    && String(dataPoints.error || '').trim().toLowerCase() === 'not_applicable';
  if (isSentinel) {
    return { kind: 'not_applicable', reason: String(dataPoints.reason || '').trim() };
  }
  const status = details && details.status;
  if (status === 'not_applicable') {
    return { kind: 'not_applicable', reason: details.not_applicable_reason || '' };
  }
  if (flattenDataPoints(dataPoints).length) {
    return { kind: 'extracted', reason: '' };
  }
  if (details && (status === 'parse_error' || status === 'error' || details.parse_error || details.error)) {
    return { kind: 'failed', reason: details.parse_error || details.error || '' };
  }
  return { kind: 'none', reason: '' };
};

export const formatValue = (value) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString() : value.toLocaleString(undefined, { maximumFractionDigits: 6 });
  }
  return String(value);
};

/**
 * DataPointsTable
 *
 * Key-agnostic renderer for extracted document data points: flattens any
 * nested JSON payload into a two-column table. Used by the Summary /
 * Data Points toggle in the SharePoint review modal and by the Document
 * Data dashboard.
 */
const DataPointsTable = ({ dataPoints, details, emptyMessage }) => {
  const extraction = getExtractionStatus(dataPoints, details);

  if (extraction.kind === 'not_applicable') {
    return (
      <div className="alert alert-secondary py-2 mb-0 small" data-testid="data-points-not-applicable">
        <i className="bi bi-slash-circle me-2"></i>
        <strong>No data extracted.</strong>{' '}
        {extraction.reason || "The extraction prompt says this document doesn't match its classified type."}
        {details && details.prompt_name && (
          <div className="text-muted mt-1">Checked with <code>{details.prompt_name}</code></div>
        )}
      </div>
    );
  }

  if (extraction.kind === 'failed') {
    return (
      <div className="alert alert-warning py-2 mb-0 small" data-testid="data-points-failed">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Data point extraction failed.</strong>{' '}
        {extraction.reason}
      </div>
    );
  }

  const rows = flattenDataPoints(dataPoints);

  if (!rows.length) {
    return (
      <div className="text-center text-muted border rounded p-3" data-testid="data-points-empty">
        <i className="bi bi-database-x me-2"></i>
        {emptyMessage || 'No data points were extracted for this document.'}
      </div>
    );
  }

  return (
    <div data-testid="data-points-table">
      <table className="table table-sm table-striped table-bordered align-middle mb-2">
        <thead>
          <tr>
            <th className="small text-muted bg-light" style={{ width: '40%' }}>Data Point</th>
            <th className="small text-muted bg-light">Value</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, value }) => (
            <tr key={key}>
              <td className="small" title={key}>{humanizeKey(key)}</td>
              <td className="small">{formatValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {details && details.prompt_name && (
        <div className="small text-muted">
          <i className="bi bi-stars me-1"></i>
          Extracted with <code>{details.prompt_name}</code>
        </div>
      )}
    </div>
  );
};

export default DataPointsTable;
